import React, { useState, useEffect, useRef } from 'react';
import '../styles/ExportModal.css';
import { getCsrfToken } from '../utils/auth';

/**
 * The export dialog — shared by the trace matrix and the Training Traceability panel.
 *
 * The two panel types post to different routes, so the endpoints are PROPS with the matrix's
 * paths as defaults. They are deliberately not forked into a second component: every guard in
 * here (`pollBusyRef`, `downloadedRef`, the `stopped` flag) exists because of the v3.8.5 bug
 * where one export downloaded 30 identical files, and a copy would be one refactor away from
 * losing them again. `ExportModal.test.jsx` pins that behaviour for whichever panel uses it.
 */
export function ExportModal({
  setupId, panelName, showTracedItems = true, keepFilterOrder = true,
  onClose, onProgressChange, onJobIdChange,
  excelUrl, htmlUrl, requestBody, excelExt = 'xlsx',
  htmlNote = 'Interactive file — expand and collapse rows, works offline. Exports all pages.',
  excelNote = 'Spreadsheet for analysis. Exports all pages — a large panel can take a minute.',
}) {
  const [format, setFormat] = useState('html');
  const excelEndpoint = excelUrl || `/api/matrix/${setupId}/export/excel`;
  const htmlEndpoint = htmlUrl || `/api/matrix/${setupId}/export/html-background`;
  // Both formats post the same body, so one builder covers them. The trace matrix sends the two
  // grid toggles so a download matches the screen it came from; Training sends nothing (its
  // export renders a cached snapshot that has no per-view toggles).
  const bodyJson = JSON.stringify(
    requestBody === undefined
      ? { show_traced_items: showTracedItems, keep_filter_order: keepFilterOrder }
      : requestBody
  );
  const mountedRef = useRef(true);
  // `mountedRef.current = true` in the BODY, not only at useRef(true): StrictMode mounts,
  // unmounts and re-mounts once in dev, and the ref survives that (same instance). A cleanup
  // that only ever sets it false leaves it false forever on the second mount, so every
  // guarded setState is silently swallowed and the modal never leaves "Processing".
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // The poll runs on a 500ms timer but each tick is an async fetch. On a slow/busy server a
  // tick can outlast its interval, so several ticks are in flight at once — and when the job
  // finishes they ALL see "completed" and each one downloads the file. Hence two guards:
  // pollBusyRef skips a tick while the previous one is still running, and downloadedRef makes
  // the download a one-shot per job whatever else happens.
  const pollBusyRef = useRef(false);
  const downloadedRef = useRef(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle');  // idle, starting, processing, completed, error
  const [message, setMessage] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Notify parent (MatrixPage) of background progress
  useEffect(() => {
    if (status === 'processing') {
      onProgressChange?.(progress);
    } else {
      onProgressChange?.(null);
    }
  }, [progress, status, onProgressChange]);

  // Reset to idle when format changes (only if not already running)
  const handleFormatChange = (newFormat) => {
    if (status === 'idle' || status === 'error') {
      setFormat(newFormat);
      setError(null);
      setStatus('idle');
    }
  };

  // Excel export: async download — modal can be closed while fetch is in flight
  const handleExcelExport = async () => {
    setStatus('starting');
    try {
      const response = await fetch(excelEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: bodyJson
      });

      if (!response.ok) {
        let message = 'Excel export failed';
        try {
          const err = await response.json();
          message = err.detail || message;
        } catch {}
        if (mountedRef.current) { setError(message); setStatus('error'); }
        return;
      }

      // Trigger download regardless of whether modal is still open
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // A blob download ignores Content-Disposition, so read the server's filename off the
      // header — otherwise every export lands as "<panel>-export.xlsx" and a second one
      // silently becomes "(1)", losing the timestamp that says which snapshot it is.
      let filename = `${panelName || 'matrix'}-export.${excelExt}`;
      const cd = response.headers.get('Content-Disposition') || '';
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      if (m && m[1]) {
        try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (mountedRef.current) setStatus('completed');
    } catch (err) {
      if (mountedRef.current) { setError('Network error: ' + err.message); setStatus('error'); }
    }
  };

  // HTML export: background job
  const handleHtmlExport = async () => {
    setStatus('starting');
    downloadedRef.current = false;   // a retry is a new job, and gets its one download
    pollBusyRef.current = false;
    try {
      const response = await fetch(
        htmlEndpoint,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken()
          },
          body: bodyJson
        }
      );

      if (response.status === 202) {
        const job = await response.json();
        setJobId(job.job_id);
        onJobIdChange?.(job.job_id);
        setStatus('processing');
        setMessage(job.message || 'Export started...');
        setRetryCount(0);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to start export');
        setStatus('error');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
      setStatus('error');
    }
  };

  const handleStartExport = () => {
    if (format === 'excel') {
      handleExcelExport();
    } else {
      handleHtmlExport();
    }
  };

  // Poll HTML export progress every 500ms
  useEffect(() => {
    if (!jobId || status !== 'processing') return;

    let pollCount = 0;
    let stopped = false;
    const stop = () => { stopped = true; clearInterval(pollInterval); };

    const pollInterval = setInterval(async () => {
      // Skip this tick while the previous request is still out. Without it, a server busy
      // building the export leaves 20+ ticks in flight; they all come back "completed" at
      // once and each downloads the file (the "24 identical files" bug).
      if (stopped || pollBusyRef.current) return;
      pollBusyRef.current = true;
      try {
        const response = await fetch(`/api/matrix/export-jobs/${jobId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const job = await response.json();
        if (stopped) return;
        setProgress(job.progress || 0);
        setMessage(job.message || '');
        setEstimatedTime(job.estimated_remaining_sec);
        setElapsedTime(job.elapsed_sec || 0);
        setRetryCount(0);

        if (job.status === 'completed') {
          stop();
          setStatus('completed');
          setDownloadUrl(job.download_url);
          setProgress(100);

          // Belt and braces: even if a tick slipped past the guards, the file is fetched
          // and handed to the browser exactly ONCE per job.
          if (downloadedRef.current) return;
          downloadedRef.current = true;

          try {
            const dlResponse = await fetch(job.download_url, {
              method: 'GET',
              credentials: 'include'
            });

            if (!dlResponse.ok) {
              throw new Error(`Download failed: HTTP ${dlResponse.status}`);
            }

            const blob = await dlResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = job.filename || `${panelName || 'export'}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (err) {
            console.error('Auto-download failed:', err);
            setError(`Auto-download failed: ${err.message}. Use the download link below.`);
          }
        } else if (job.status === 'error') {
          stop();
          setStatus('error');
          setError(job.error || 'Export failed');
        }
      } catch (err) {
        pollCount++;
        if (pollCount > 5) {
          stop();
          setStatus('error');
          setError('Lost connection to server. Export may still be processing.');
        } else {
          setRetryCount(pollCount);
        }
      } finally {
        pollBusyRef.current = false;
      }
    }, 500);

    return () => { stopped = true; clearInterval(pollInterval); };
  }, [jobId, status]);

  const handleCancel = async () => {
    if (jobId && status === 'processing') {
      try {
        // `get_session` enforces X-CSRF-Token on DELETE as well as POST — without it this
        // cancel 403s and the job keeps running while the UI says it was cancelled.
        await fetch(`/api/matrix/export-jobs/${jobId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'X-CSRF-Token': getCsrfToken() }
        });
      } catch (err) {
        console.error('Failed to cancel export:', err);
      }
    }
    onClose();
  };

  const isRunning = status === 'processing' || status === 'starting';
  const fileExt = format === 'excel' ? excelExt : 'html';

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <div className="export-modal-header">
          <h2>Export</h2>
          <button
            className="export-modal-close"
            onClick={() => onClose(status === 'processing')}
            title="Close"
          >
            ×
          </button>
        </div>

        {status === 'idle' && (
          <div>
            <div className="export-format-selector">
              <label className={`export-format-option ${format === 'html' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="export-format"
                  value="html"
                  checked={format === 'html'}
                  onChange={() => handleFormatChange('html')}
                />
                <span className="format-icon">🌐</span>
                <span className="format-label">HTML</span>
                <span className="format-desc">Interactive file with expand/collapse</span>
              </label>
              <label className={`export-format-option ${format === 'excel' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="export-format"
                  value="excel"
                  checked={format === 'excel'}
                  onChange={() => handleFormatChange('excel')}
                />
                <span className="format-icon">📊</span>
                <span className="format-label">Excel</span>
                <span className="format-desc">Spreadsheet for data analysis</span>
              </label>
            </div>

            {format === 'html' && (
              <p style={{ marginTop: '12px' }}>{htmlNote} You can continue working while the export runs in the background.</p>
            )}
            {format === 'excel' && (
              <p style={{ marginTop: '12px' }}>{excelNote} The file downloads when it is ready, even if you close this window.</p>
            )}

            <div className="button-group">
              <button className="btn-primary" onClick={handleStartExport}>
                Export {format === 'excel' ? 'Excel' : 'HTML'}
              </button>
              <button className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {status === 'starting' && (
          <div>
            <p style={{ color: '#2196f3', fontSize: '14px', marginBottom: '12px' }}>
              ✓ {format === 'excel' ? 'Generating Excel file...' : 'Initializing export...'}
            </p>
            <p style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
              You can close this window. The file will download automatically when ready.
            </p>
            <div className="button-group">
              <button className="btn-secondary" onClick={() => onClose(false)}>
                Close & Continue Working
              </button>
            </div>
          </div>
        )}

        {status === 'processing' && (
          <div>
            <p style={{ color: '#2196f3', fontSize: '14px', marginBottom: '12px' }}>
              ✓ Export running in background
            </p>

            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-text">{progress}%</span>
            </div>

            <p className="status-message">{message}</p>

            <div className="time-info">
              {elapsedTime > 0 && (
                <p>Elapsed: {Math.round(elapsedTime)}s</p>
              )}
              {estimatedTime > 0 && (
                <p>Est. remaining: {Math.round(estimatedTime)}s</p>
              )}
              {retryCount > 0 && (
                <p style={{ color: '#ff9800' }}>
                  Connection issues (retry {retryCount}/5)
                </p>
              )}
            </div>

            <p style={{ color: '#666', fontSize: '12px', marginTop: '12px', fontStyle: 'italic' }}>
              You can close this window and continue working. The file will download automatically when ready.
            </p>

            <div className="button-group">
              <button className="btn-secondary" onClick={() => onClose(true)}>
                Close & Continue Working
              </button>
              <button className="btn-danger" onClick={handleCancel}>
                Cancel Export
              </button>
            </div>
          </div>
        )}

        {status === 'completed' && (
          <div>
            <p style={{ color: '#4caf50', fontSize: '16px', fontWeight: 'bold' }}>
              Export Complete!
            </p>
            <p>Your file has been downloaded.</p>
            <div className="button-group">
              <button className="btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div>
            <p style={{ color: '#f44336', fontSize: '14px' }}>
              Error: {error}
            </p>
            {error?.includes('export in progress') && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', color: '#856404', margin: '0 0 12px 0' }}>
                  There's a stuck export. Click "Stop Process" to force-cancel it, then retry.
                </p>
                <button
                  className="btn-danger"
                  onClick={async () => {
                    try {
                      const response = await fetch('/api/matrix/export-jobs/clear-stuck', {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-CSRF-Token': getCsrfToken()
                        }
                      });
                      if (response.ok) {
                        setError(null);
                        setStatus('idle');
                      } else {
                        setError('Failed to stop process');
                      }
                    } catch (err) {
                      setError('Network error: ' + err.message);
                    }
                  }}
                  style={{ width: '100%' }}
                >
                  Stop Process
                </button>
              </div>
            )}
            <div className="button-group">
              <button className="btn-primary" onClick={() => { setStatus('idle'); setError(null); }}>
                Retry
              </button>
              <button className="btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
